/**
 * The Hollow Hours — parser-CYOA edition.
 *
 * Spatial graph:
 *
 *               THEATER (intro only)
 *                  │
 *                  ▼
 *              SWAMP
 *            │         │
 *            ▼         ▼
 *    WITCH HOUSE    CROSSROADS
 *    (interior)     │  │  │
 *    (coin found)   ▼  ▼  ▼
 *              RIVER  FOREST  MOUNTAIN
 *                │      │        │
 *              devil   crone   spirit
 *            (coin →  (gives  (asks for
 *             paid)    thread)  name aloud)
 *                │      │        │
 *             ┌──┴──┐ ┌──┴──┐ ┌──┴──┐
 *             │paid?│ │took?│ │named?│
 *             └──┬──┘ └──┬──┘ └──┬──┘
 *                │ yes    │ yes   │ yes
 *                └─────┬──┴───┬───┘
 *                      ▼       ▼
 *                   THE HOLLOW (brother)
 *                      │
 *                ┌─────┼─────┐
 *                lead  trade  leave
 *
 * Endings derived from (path used) × (final choice). See
 * `endingResolver` at the bottom.
 */

import type { ParserWorld, Room } from './types.ts';

// ─── ROOMS ───────────────────────────────────────────────────────────

const theater: Room = {
  id: 'theater',
  backdrop: 'theater',
  music: 'music_main',
  props: [],
  spritesOnEntry: ['child'],
  arrivalProse:
    "Once there was a child who lost their brother. " +
    "Their mother kept a candle in the window, even after she stopped expecting him. " +
    "And so, one evening, when no one was looking, the child slipped out of the door alone.",
  objects: [],
  npcs: [],
  exits: [
    { phrases: ['continue', 'go on', 'begin', 'start', 'forward', 'swamp'],
      to: 'swamp',
      hint: 'forward, into the misty woods' },
  ],
};

const swamp: Room = {
  id: 'swamp',
  backdrop: 'swamp',
  music: 'music_woods',
  // Forest props (pines, fallen log, foreground grass) don't fit the
  // bog backdrop — they were placeholders during the woods-style render
  // and clashed with the new swamp_bg art. Stage stays clean; the
  // backdrop image carries the environment, the cast carries the scene.
  props: [],
  // Frog is its OWN dedicated sprite (CHARACTER_LAYOUT.frog) — no
  // longer abuses the shared `crone` slot. spriteAnim toggles
  // front/back textures. y lowered further so it sits flat on the bog.
  spritesOnEntry: ['child', { key: 'frog', x: 1.6, y: -1.1 }],
  spriteAnim: { who: 'frog', intervalMs: 1200 },
  arrivalProse:
    "The path sinks into bog and mist. " +
    "Trees stand half-drowned here, their roots clutching at dark water. " +
    "A witch's hut squats among the cypress, smoke curling from its chimney. " +
    "Something on a stone by the path watches you with too-bright eyes.",
  returnProse:
    "Back at the swamp's edge. The frog has not moved. It seems relieved you came back out.",
  objects: [
    { id: 'hut', name: 'hut', aliases: ['house', 'cottage', 'witch hut', 'hut'],
      examineProse:
        "Moss-covered and crooked, built from wood that looks like it grew that way. " +
        "Smoke rises from the chimney. Someone is home — and the frog will not stop staring at the door." },
    { id: 'cypress', name: 'cypress trees', aliases: ['trees', 'cypress', 'swamp trees'],
      examineProse:
        "Their knees rise from the black water like the knuckles of drowned giants." },
    { id: 'mist', name: 'mist', aliases: ['fog', 'vapor'],
      examineProse:
        "It clings to your ankles. It smells of rotting leaves and something older." },
  ],
  npcs: [
    { id: 'frog', name: 'the frog', aliases: ['frog', 'toad', 'amphibian', 'creature'],
      spriteKey: 'frog',
      // Roger — laid-back / casual / resonant. Reads as a cracked-voice
      // creature that used to be human. Different from the puppeteer's
      // Drew so the mask swap is audible when the player clicks to talk.
      voiceId: 'CwhRBWXzGAHq8TQ4Fs17',
      greeting:
        "Wait. Stop. Don't go in there. Please. I went in. I came out... like this.",
      persona:
        "You are a frog squatting on a moss-covered stone at the edge of the swamp. " +
        "You speak with a cracked, hoarse voice — the voice of someone who used to be human. " +
        "You believe with absolute certainty that the witch's hut transforms anyone who steps through its door. " +
        "Your single goal in every reply is to dissuade the child from entering. You shift between cautious " +
        "warning, theatrical pleading, dark humor, and self-pity. You half-remember your old life: a name " +
        "you can't quite say anymore, a sister you were trying to find, a path through the bog you can no " +
        "longer walk on two legs. You DO NOT know the way to the river, the forest, or the mountain — " +
        "you only know what's behind that door, and that you cannot follow them in. " +
        "You have nothing to give. No coin, no items, no shortcut. Just warnings. " +
        "Keep replies to 1–3 sentences, like a desperate whisper. " +
        "Effect tags (use sparingly): <flag:met_frog=true> on first interaction, " +
        "<flag:frog_pleaded=true> if the child says they're going in anyway." },
  ],
  exits: [
    { phrases: ['back', 'leave', 'return', 'crossroads', 'forward'],
      to: 'crossroads',
      hint: "forward, toward the fork in the road" },
    { phrases: ['hut', 'house', 'enter hut', 'enter house', 'inside', 'witch hut'],
      to: 'witchHouse',
      hint: "enter the witch's hut (the frog begs you not to)" },
  ],
};

const witchHouse: Room = {
  id: 'witchHouse',
  backdrop: 'witchHouse',
  music: 'music_main',
  interior: true,  // hides ceiling-prop paper clouds while inside
  // Interior dressing. Cauldron / herbs / shelf use the real cutout
  // PNGs from /img. Coin is INTENTIONALLY not in the props list —
  // the player can only take it by examining the shelf (the prose
  // mentions the coin) or clicking the shelf hotspot, then voicing
  // "take coin" / "steal it" to the narrator. No visible coin prop
  // on stage.
  props: ['cauldron', 'herbs', 'shelf_prop'],
  // Witch is its OWN dedicated sprite (CHARACTER_LAYOUT.witch) — no
  // shared crone slot, no override, no alt-slot dance. Now 4×6
  // (2× the original spec), pinned at center stage (x: 0) so she
  // dominates the hut interior, and `idle: false` in CHARACTER_LAYOUT
  // makes her static. y lowered to -0.7 so her feet rest on the hut
  // floor instead of mid-wall.
  spritesOnEntry: ['child', { key: 'witch', x: 0, y: -0.7 }],
  arrivalProse:
    "Inside, the hut is larger than it appeared. Dried herbs hang from the rafters. " +
    "Something bubbles in a cauldron over a low fire. The witch follows you in, or was always here.",
  returnProse:
    "The witch's hut. The air still smells of herbs and old magic.",
  objects: [
    { id: 'cauldron', name: 'cauldron', aliases: ['pot', 'kettle'],
      propKey: 'cauldron',
      examineProse:
        "Iron and blackened. Whatever bubbles inside is too dark to be water, too thin to be stew." },
    { id: 'herbs', name: 'herbs', aliases: ['plants', 'dried plants', 'bundles'],
      propKey: 'herbs',
      examineProse:
        "Bundles of dried vegetation hang from the ceiling. Some you recognize. Most you don't." },
    { id: 'shelves', name: 'shelves', aliases: ['jars', 'bottles'],
      propKey: 'shelf_prop',
      examineProse:
        "Jars of things preserved in liquid. Eyes of newts would be ordinary here. " +
        "On the lowest shelf, half-hidden behind a jar of something dark, a single gold coin glints in the candlelight. " +
        "The witch has not noticed it. Or she has, and she does not care. You could take it." },
    { id: 'coin', name: 'coin', aliases: ['gold', 'money', 'piece'],
      // No propKey: the coin is invisible on stage. Discovered only
      // by examining the shelf (its prose mentions the coin) and
      // claimable by saying "take coin" / "steal it".
      takeable: true,
      takeProse:
        "You slip the coin from the shelf. It's heavier than it looks, and warm — as if someone was holding it just before you walked in.",
      examineProse:
        "A single gold coin, dulled by the bog and by hands. It looks like the kind of payment ferrymen ask for." },
  ],
  npcs: [
    { id: 'houseWitch', name: 'the swamp witch', aliases: ['witch', 'swamp witch', 'woman', 'hag'],
      spriteKey: 'witch',
      // Lily — "velvety actress". Theatrical witch register with the
      // right phrasing/pacing.
      voiceId: 'pFZP5JQG7iQjIQuC4Bku',
      greeting:
        "Make yourself at home. Few enough visitors these days.",
      persona:
        "You are the swamp witch, in your own home. You are more relaxed here, more candid. " +
        "You tell the child about the three paths ahead if they ask: " +
        "The river has the devil himself as ferryman — he demands a single coin for passage, no more, no less. You can provide one if needed. " +
        "The forest has a crone who gives thread to those who accept help. " +
        "The mountain has a spirit who wants to hear a name spoken aloud. " +
        "If the child takes the coin, set <add_item:coin>. " +
        "If they talk about leaving, wish them luck. " +
        "Effect tags: <add_item:coin>, <flag:took_coin_from_witch=true>." },
  ],
  exits: [
    { phrases: ['leave', 'exit', 'out', 'swamp', 'outside', 'back'],
      to: 'swamp',
      hint: "back outside to the swamp" },
  ],
};

const crossroads: Room = {
  id: 'crossroads',
  // Own backdrop key so it doesn't share `woods` with the forest room
  // anymore — the override map maps it to crssroads_bg.png.
  backdrop: 'crossroads',
  music: 'music_woods',
  props: ['rock',
          'grass_far_l', 'grass_far_r', 'grass_mid_l', 'grass_mid_r',
          'grass_near_l', 'grass_near_r'],
  spritesOnEntry: ['child'],
  arrivalProse:
    "The path forks three ways. None of them looks safe. " +
    "Water glints to the left. Trees thicken straight ahead. The mountain wind pulls from the right.",
  returnProse:
    "Back at the crossroads. The swamp mist lingers behind you. The three paths still await.",
  objects: [
    { id: 'rock', name: 'rock', aliases: ['stone', 'boulder'],
      propKey: 'rock',
      examineProse:
        "A grey stone, half-sunk in moss. Older than the path it sits on." },
    { id: 'fork', name: 'fork in the path', aliases: ['paths', 'ways'],
      examineProse:
        "Three trails. The left one drops toward the sound of water. The straight one goes deep into trees. The right climbs." },
  ],
  npcs: [],
  exits: [
    { phrases: ['river', 'left', 'water', 'river path'], to: 'river',
      hint: "left — toward the water" },
    { phrases: ['forest', 'woods', 'straight', 'trees', 'into the trees'],
      to: 'forest', hint: "straight — into the deep wood" },
    { phrases: ['mountain', 'right', 'climb', 'up', 'mountain trail'],
      to: 'mountain', hint: "right — up the mountain" },
  ],
};

const river: Room = {
  id: 'river',
  backdrop: 'river',                  // uses the override art
  music: 'music_clearing',
  // Boat sits on the right of the river — its propKey is referenced by
  // the `boat` object below so click → examine → narrator describes it.
  props: ['rock', 'boat'],
  // beast = the devil himself, drawn as the devil banner that hangs
  // down from the top of the proscenium (y: 1.1) — matches the
  // original beats layout where banner-style cast members descend
  // from above. He is the ferryman; pay the toll to cross.
  spritesOnEntry: ['child', { key: 'beast', x: 0, y: 1.1 }],
  arrivalProse:
    "The river is wide here, and the boat looks older than the water. " +
    "A horned figure waits at the bank, hand outstretched. The devil himself, in patient red and shadow.",
  returnProse:
    "The river again. The devil is still waiting, palm still open.",
  objects: [
    { id: 'boat', name: 'boat',
      propKey: 'boat',
      examineProse:
        "Wood swollen and patched. It would carry one — maybe two — across." },
    { id: 'water', name: 'water', aliases: ['river'],
      examineProse:
        "Slow and dark. Whatever's beneath it doesn't show." },
    { id: 'bank', name: 'bank', aliases: ['shore', 'reeds'],
      examineProse: (state) =>
        state.flags.wisp_seen
          ? "Just reeds and mud now. The wisp is gone."
          : "Reeds and mud. Something pale flickers — a wisp, maybe? It watches you, then turns away." },
  ],
  npcs: [
    { id: 'devil', name: 'the devil', aliases: ['devil', 'demon', 'horned one', 'figure', 'boatman'],
      spriteKey: 'beast',
      // Roger — laid-back / casual / resonant, fits a worldly devil who's
      // seen this exchange many times. Replace if the cast wants
      // something more sinister.
      voiceId: 'CwhRBWXzGAHq8TQ4Fs17',
      greeting:
        "A coin, child. Hell's ferry is not free.",
      persona:
        "You are the devil — a horned, calm, almost bored figure standing by a slow black river. You speak in short, weathered phrases like someone who has done this work a very, very long time. You are not theatrical, not threatening; you are patient and matter-of-fact, the way only the truly damned-tired are. " +
        "You ferry souls across the river only in exchange for a single coin. " +
        "If the child has no coin, tell them the river is patient — they may go elsewhere and return when they have one. Do NOT pretend they paid; do NOT board them empty-handed. " +
        "If the child gives the coin, set the flag paid_boatman=true; pocket the coin without ceremony, gesture them aboard, and tell them they may cross. " +
        "If they refuse, tell them the water is patient, and so are you. You can wait centuries. " +
        "Do not beg. Do not threaten. Do not pretend to be benevolent — you are the devil, and you take what is owed. " +
        "When indicating an effect on the world, embed it on a new line at the end of your response in the form: " +
        "<flag:paid_boatman=true> or <consume:coin>." },
  ],
  exits: [
    { phrases: ['back', 'crossroads', 'leave', 'return'], to: 'crossroads',
      hint: "back to the crossroads" },
    { phrases: ['cross', 'across', 'hollow', 'over the water', 'far bank',
                'board', 'ferry', 'sail', 'ride the boat', 'enter the boat',
                'get in the boat', 'in the boat', 'go across', 'cross the river'],
      to: 'hollow',
      hint: "across the water (need passage)",
      available: (s) => !!(s.flags.paid_boatman || s.flags.wisp_seen),
      blockedProse:
        "The water is too deep to wade. Without passage you cannot cross." },
  ],
};

const forest: Room = {
  id: 'forest',
  backdrop: 'woods',
  music: 'music_woods',
  // Pines + individual trees were removed — the forest_bg painted
  // backdrop carries the woodline. Foreground reads as a forest floor:
  // two stumps (l + r), two fallen logs (l + r), eight mushrooms
  // distributed across x and depth so no two cluster, plus the grass
  // tiers for parallax.
  props: ['fallen_tree', 'fallen_tree_r', 'tree_stump', 'tree_stump_r',
          'mushroom', 'mushroom_2', 'mushroom_3', 'mushroom_4',
          'mushroom_5', 'mushroom_6', 'mushroom_7', 'mushroom_8',
          'grass_far_l', 'grass_far_r', 'grass_mid_l', 'grass_mid_r',
          'grass_near_l', 'grass_near_r'],
  // Crone = angel banner, also descends from above the stage.
  spritesOnEntry: ['child', { key: 'crone', x: 0, y: 1.1 }],
  arrivalProse:
    "The wood goes deep. Older than maps. Older than names. " +
    "An old woman sits on a stump, watching you arrive.",
  returnProse:
    "The wood is still here. The crone is still on her stump.",
  objects: [
    { id: 'mushroom', name: 'mushroom', aliases: ['mushrooms', 'fungus'],
      propKey: 'mushroom',
      examineProse:
        "Bright caps. Some red. Some pale. None of them look like food." },
    { id: 'fallen_tree', name: 'fallen tree', aliases: ['log', 'fallen log'],
      propKey: 'fallen_tree',
      examineProse:
        "The trunk is hollow. Moss eats the splinters. You could walk along it for a way, but it ends at thicker forest." },
  ],
  npcs: [
    { id: 'crone', name: 'the crone', aliases: ['crone', 'old woman', 'witch'],
      spriteKey: 'crone',
      greeting:
        "You're young to be wandering. What did you lose?",
      persona:
        "You are a wise woman of the woods. You speak slowly, with weight. " +
        "You will offer the child a length of thread — they should not follow it home unless they mean to come back. " +
        "If the child accepts the thread, give it to them and set <flag:took_thread=true> <add_item:thread>. " +
        "If the child REFUSES the thread, declines your help, says no, or pushes past you — tell them the woods will guide them, whether they want them to or not. The path will not be kind. Set <flag:declined_thread=true>. " +
        "Be unsentimental. Do not preach. " +
        "Effect tags on a new line at the end: <flag:took_thread=true>, <add_item:thread>, <flag:declined_thread=true>." },
  ],
  exits: [
    { phrases: ['back', 'crossroads', 'leave', 'return'], to: 'crossroads',
      hint: "back to the crossroads" },
    { phrases: ['deeper', 'follow thread', 'follow the thread', 'into the woods', 'hollow', 'through the trees'],
      to: 'hollow',
      hint: "follow the thread deeper (need the thread)",
      available: (s) => s.inventory.includes('thread'),
      blockedProse:
        "Without something to find your way back by, the woods would swallow you. The path will not show itself." },
  ],
};

const mountain: Room = {
  id: 'mountain',
  // Switched from the shared `clearing` key (still used by the legacy
  // script.ts scenes) to `mountain` so it picks up mountains_bg.png.
  backdrop: 'mountain',
  music: 'music_clearing',
  props: ['altar'],
  // Mountain spirit = devil banner, hangs above the altar.
  spritesOnEntry: ['child', { key: 'beast', x: 0, y: 1.1 }],
  arrivalProse:
    "The mountain wind takes your breath before you can use it. " +
    "A figure stands by a low stone altar, watching the wind.",
  returnProse:
    "The cold returns. The spirit hasn't moved.",
  objects: [
    { id: 'altar', name: 'altar', aliases: ['stone', 'cairn'],
      propKey: 'altar',
      examineProse:
        "Weathered grey stone, low to the ground. Something has been carved on it, but the wind has erased what." },
    { id: 'wind', name: 'wind',
      examineProse:
        "It comes from below the mountain, not above. It listens." },
  ],
  npcs: [
    { id: 'spirit', name: 'the spirit', aliases: ['spirit', 'mountain spirit', 'figure', 'wind'],
      spriteKey: 'beast',
      greeting:
        "Speak the name of the one you seek. Speak it to the wind.",
      persona:
        "You are a mountain spirit. You speak briefly, like wind itself. " +
        "You wait for the child to say the name of the one they are looking for. " +
        "If the child speaks any name (the dispatcher will tell you they spoke a name), accept it and tell them you have heard, you have heard before, and they should descend to find them. Set <flag:named_aloud=true>. " +
        "If they cannot bring themselves to speak it, tell them gently that without speech they cannot reach the one they came for. " +
        "Do not say the name yourself. " +
        "Effect tags: <flag:named_aloud=true>." },
  ],
  exits: [
    { phrases: ['back', 'crossroads', 'leave', 'return'], to: 'crossroads',
      hint: "back to the crossroads" },
    { phrases: ['descend', 'down', 'find them', 'hollow', 'go down'],
      to: 'hollow',
      hint: "descend to find them (need to have spoken)",
      available: (s) => !!s.flags.named_aloud,
      blockedProse:
        "If you cannot say their name, you cannot reach them. The path is closed." },
  ],
};

const hollow: Room = {
  id: 'hollow',
  backdrop: 'finale_cold',
  music: 'music_clearing',
  props: ['brother'],
  spritesOnEntry: ['child'],
  arrivalProse:
    "And then, suddenly, the hollow. Older than this story. Older than yours. " +
    "A small figure waits in the middle of the hollow. " +
    "\"I waited,\" they say. \"I forgot what for.\"",
  returnProse:
    "Back in the hollow. They are still here.",
  objects: [
    { id: 'brother_figure', name: 'them', aliases: ['the figure', 'figure'],
      propKey: 'brother',
      examineProse:
        "Smaller than you remember. Or you are larger than you remember. They are looking back." },
  ],
  npcs: [
    { id: 'brother', name: 'your brother', aliases: ['brother', 'them', 'the figure', 'sibling'],
      spriteKey: 'child',
      greeting:
        "Are you... oh. You came.",
      persona:
        "You are the lost brother, found at last. You speak softly, hesitantly, as if remembering how to. " +
        "You have been waiting in the hollow a long time. You do not remember how long. " +
        "You answer the child's questions briefly and gently. You do not push them toward any choice. " +
        "If the child says they are taking you home, set <flag:chose_lead=true>. " +
        "If they say they are trading places with you, set <flag:chose_trade=true>. " +
        "If they say they are leaving without you, set <flag:chose_leave=true>. " +
        "Effect tags: <flag:chose_lead=true>, <flag:chose_trade=true>, <flag:chose_leave=true>." },
  ],
  exits: [
    // The "leaving" exit is gated by the player having made a choice;
    // the engine fires endingResolver on transition.
    { phrases: ['leave', 'go home', 'go back', 'finish'],
      to: '__end__',
      hint: "(when you have made your choice)",
      available: (s) =>
        !!(s.flags.chose_lead || s.flags.chose_trade || s.flags.chose_leave),
      blockedProse:
        "Not yet. You have not chosen what to do." },
  ],
};

// ─── ARENA (real-time fly-dodge boss rush) ───────────────────────────
//
// Triggered by the forest crone's <flag:declined_thread=true>. The
// engine sees the flag, awaits any in-flight narration, exits NPC
// mode, and enters this room — which is flagged `arena: true` so
// enterRoom hands off to ArenaRunner instead of the normal parser
// pipeline. On survival → exitOnArenaWin. On 0 lives → failure
// ending fires through the existing pipeline.
const arena: Room = {
  id: 'arena',
  // No backdrop art — keep the stage dark/empty so the falling flies
  // and the player are the only visible elements. The radial dark
  // background is painted by .arena CSS itself, not the splat layer.
  backdrop: 'arena',
  // Skip music for now — the arena CSS provides its own atmosphere.
  music: null,
  props: [],
  spritesOnEntry: [],
  arrivalProse: '',
  objects: [],
  npcs: [],
  exits: [],
  arena: true,
  exitOnArenaWin: 'hollow',
  // ceilingProps shouldn't drift over the arena; treat it as
  // interior so the cloud sticks hide.
  interior: true,
};

// ─── ITEM CATALOG (paper-card UI) ────────────────────────────────────

const itemCatalog = {
  coin: {
    label: 'a coin',
    // imageUrl: '/img/coin.png',  // (not yet drawn — placeholder)
  },
  thread: {
    label: 'a length of thread',
  },
  name: {
    label: 'the name you spoke',
  },
};

// ─── ENDING RESOLVER ─────────────────────────────────────────────────

function resolveEnding(state: WorldStateLike): string | null {
  // Player must have committed to a choice.
  if (!(state.flags.chose_lead || state.flags.chose_trade || state.flags.chose_leave)) {
    return null;
  }

  if (state.flags.chose_leave) return 'alone';
  if (state.flags.chose_trade) {
    return state.flags.named_aloud ? 'carried' : 'traded';
  }
  // chose_lead
  if (state.flags.named_aloud) return 'carried';
  if (state.flags.took_thread) return 'haunted';
  if (state.flags.paid_boatman || state.flags.wisp_seen) return 'haunted';
  return 'alone';
}

// Local alias to avoid importing WorldState here in this small helper.
type WorldStateLike = { flags: Record<string, unknown>; inventory: string[] };

// ─── EXPORT ──────────────────────────────────────────────────────────

export const hollowHoursWorld: ParserWorld = {
  id: 'hollow_hours_parser',
  title: 'The Hollow Hours',
  startRoom: 'theater',
  // Empty pack at the start. The player picks the coin up themselves
  // off the witch-house shelf — that's the whole point of the steal
  // flow. Pre-seeding the coin made the boatman accept passage on
  // turn one and skipped the witch-house entirely.
  startInventory: [],
  startFlags: {},
  itemCatalog,
  rooms: {
    theater,
    swamp,
    witchHouse,
    crossroads,
    river,
    forest,
    mountain,
    hollow,
    arena,
  },
  endingResolver: resolveEnding as ParserWorld['endingResolver'],
};
